import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAbsoluteUrl } from '@shared/lib/is-absolute-url';
import { useOutsideClick } from '@shared/lib/use-outside-click';
import Share from './assets/share.svg';
import { type ISocialLinks } from '../model/types';
import { sharedButtonsScheme } from '../model/sharedButtonsScheme';

export function useSocialShare() {
  const [isAction, setIsAction] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeWithTimeout = () => {
    timeoutRef.current = setTimeout(() => {
      setIsAction(false);
    }, 400);
  };

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return { isAction, setIsAction, closeWithTimeout, resetTimeout };
}

const getAbsoluteUrl = (currentLink: string, mainDomain: string) => {
  if (isAbsoluteUrl(currentLink)) {
    return currentLink;
  }
  const host = `https://${mainDomain}`;
  return new URL(currentLink, host).toString();
};

interface ISocialShare {
  title: string;
  image: string;
  description: string;
  link: string;
  mainDomain: string;
  socials: ISocialLinks;
}

export function SocialShare({
  title,
  image,
  description,
  link,
  mainDomain,
  socials,
}: ISocialShare) {
  const { t } = useTranslation();
  const { isAction, setIsAction, closeWithTimeout, resetTimeout } =
    useSocialShare();
  const { outsideClickRef } = useOutsideClick(
    () => setIsAction(false),
    isAction,
  );

  const imgPath = `/img/${image}`;

  const shareLink = getAbsoluteUrl(link, mainDomain);
  const shareImage = getAbsoluteUrl(imgPath, mainDomain);

  const buttonsScheme = sharedButtonsScheme(description, shareImage);
  const filteredSocials = buttonsScheme.filter((item) => item.id in socials);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsAction(!isAction)}
        className={clsx('cursor-pointer fill-gray hover:fill-(--primary-2)', {
          'fill-(--primary-2)!': isAction,
        })}
      >
        <Share width={22} height={22} />
      </button>
      {isAction && (
        <div
          ref={outsideClickRef}
          onMouseLeave={closeWithTimeout}
          onMouseEnter={resetTimeout}
          className="absolute right-0 bottom-10 flex max-w-75 flex-col gap-2 rounded-sm border
            border-gray bg-white p-5"
        >
          <span
            className="absolute right-3.5 -bottom-[11px] h-5 w-5 rotate-45 border border-t-0 border-l-0
              border-gray bg-white"
          />
          <div className="text-center text-sm text-(--verydarkgray)">
            {t('SocialShare.select_share_network')}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {filteredSocials.map(
              ({ id, ButtonComponent, Icon, additionalProps }) => (
                <ButtonComponent
                  key={id}
                  url={shareLink}
                  title={title}
                  windowWidth={600}
                  windowHeight={400}
                  className="fill-(--secondary-2)! opacity-70 hover:opacity-100"
                  {...additionalProps}
                >
                  <Icon width={40} height={40} />
                </ButtonComponent>
              ),
            )}
          </div>
        </div>
      )}
    </>
  );
}
